const journeys = {
  journeyWebsite: {
    skipToContent: "Skip to main content",
    header: {
      tagline: "WHOLE-PERSON CARE",
      startTesting: "Start testing",
      navHowItWorks: "How it works",
      navJourneys: "Journeys",
      navAbout: "About",
    },
    faq: {
      title: "FAQ",
      subtitle:
        "Find answers to your questions about our personalized wellness programs and services.",
      whatIncludedQuestion: 'What does a "Journey" include?',
      whatIncludedAnswer:
        "A Journey is a personalized wellness plan that goes beyond standard lab work. You'll receive comprehensive lab testing, detailed interpretations, a provider-crafted wellness plan covering nutrition, movement, stress management, and targeted supplements to meet your goals. With follow-up testing and reminders built in to keep you on track, everything lives in one place, so staying on top of your health is easy.",
      differenceQuestion:
        "What makes Journeys different from a regular checkup or wellness program?",
      differenceAnswer:
        "A regular checkup is designed to test baseline health markers and flag problems once they've already developed. Journeys takes a proactive, preventative approach — testing a broader set of biomarkers to catch early signs of imbalances before they become bigger issues. It's built for people who want to take control of their health and live healthier, longer.",
      optimalRangesQuestion:
        "What's the difference between functional optimal ranges and conventional lab ranges?",
      optimalRangesAnswer:
        'Conventional lab ranges are designed to identify disease — if your results fall within the "normal" range, you\'re typically told everything looks fine. Functional optimal ranges are narrower and based on clinical research tied to long-term health outcomes. This means your results may flag areas for improvement that a standard lab test would miss, giving you a chance to act earlier.',
      hsaFsaQuestion: "Is this HSA and FSA Eligible?",
      hsaFsaAnswer:
        "With Journeys everything is covered in one transparent price with no hidden fees or surprise costs. Many HSA and FSA accounts can be used to cover the cost, but please check with your plan administrator for eligibility.",
      schedulingQuestion: "How do I schedule my lab tests?",
      schedulingAnswer:
        "After purchasing your lab test, you'll receive an email with clear instructions and a link to schedule your appointment. We've partnered with Quest Diagnostics, offering over 2,000 locations nationwide, making it easy to coordinate your blood draw.",
      fullscriptQuestion: "What is Fullscript?",
      fullscriptAnswer:
        "Fullscript is trusted by over 125,000 providers and is the platform that powers your Journey. It's where your provider manages your care end-to-end — from ordering your lab tests and crafting your wellness plan, to sending you updates, appointment reminders, and alerts when your results are ready.",
    },
    shareSection: {
      title: "Share ",
      titleItalic: "Journeys",
      subtitle: "Invite friends and family to start their Journey today!",
      bulletInterpretations: "Provider-reviewed interpretations",
      bulletRanges: "Functional optimal ranges",
      bulletNoSubscription: "No subscription required",
      bulletHsaFsa: "HSA/FSA eligible",
      sendInvite: "Send invite",
      emailSubject: "Start your health Journey on Fullscript",
      emailBodyPrefix:
        "I wanted to share these health Journeys with you. Check them out and get started here: ",
      shareTitle: "Start your health Journey on Fullscript",
    },
    howItWorks: {
      eyebrow: "The process",
      title: "How it works",
      step01Label: "Step 01",
      step01Title: "Book your lab test",
      step01Description:
        "Choose a panel and complete a brief health intake form. We'll help coordinate your lab appointment at a nearby partner clinic.",
      step02Label: "Step 02",
      step02Title: "Get your results interpreted",
      step02Description:
        "We'll analyze your results using functional optimal ranges instead of conventional reference ranges that catch problems but miss the early warning signs.",
      step03Label: "Step 03",
      step03Title: "Follow your wellness plan",
      step03Description:
        "Receive a personalized wellness plan with actionable steps across nutrition, movement, supplements, and stress management, built around your goals.",
    },
    hero: {
      eyebrow: "Functional Health Journeys",
      headline: "No one should navigate their health alone",
      subheadline:
        "With {{-pracName}}, you get more than lab results — you get a clear path forward. Every Journey combines advanced lab testing with a provider-reviewed wellness plan personalized to your results, giving you real direction on reaching your goals.",
      sentBy: "Sent by {{-pracName}}",
      startTesting: "Start testing",
      badgeClinical: "Clinically grounded",
      badgeProvider: "Provider-reviewed",
      badgeHsaFsa: "HSA/FSA eligible",
    },
    panelGrid: {
      eyebrow: "All Journeys ({{count}})",
      title: "Built for <italic>you</italic>",
      subtitle:
        "Whether you're trying to understand your baseline or dive deep into a specific health area, we have the right lab panel for you.",
      showMore: "Show more ({{count}})",
      biomarkers: "{{count}} biomarkers",
      benefitInterpreted: "Results interpreted by your provider",
      benefitLocations: "2,000+ Quest blood draw locations",
      benefitPlan: "Personalized wellness plan",
      viewPanel: "View panel",
      checkout: "Checkout",
    },
    providerSection: {
      eyebrow: "Your practitioner",
      photoAlt: "Photo of {{-pracName}}",
    },
    valueProp: {
      eyebrow: "About",
      title: "Care that looks for the <italic>why</italic>, not just the <italic>what</italic>",
      bodyParagraph1:
        "Conventional lab panels are designed to detect disease. Our panels are designed to optimize health. There's a meaningful difference — and it starts with testing the right things, in the right context, and interpreting them through a functional lens.",
      bodyParagraph2:
        "Every journey is provider-reviewed, clinically grounded, and delivered in plain language. You get more than data, you get a clear plan of action built around your biology.",
      proofClinicalTitle: "Clinically grounded",
      proofClinicalDescription: "Every biomarker is tied to clinical relevance",
      proofRangesTitle: "Optimal ranges",
      proofRangesDescription: "Catch what conventional labs miss",
      proofReviewedTitle: "Provider-reviewed",
      proofReviewedDescription: "Human interpretation across every result",
      proofActionTitle: "Action-oriented",
      proofActionDescription: "A plan that gives you what to do next",
      lifestyleImageAlt: "Woman sitting on a couch checking her wellness plan on her phone",
      notificationTitle: "New message from {{-pracName}}:",
      notificationBody: "It's time to book your follow-up lab, and track your progress!",
      notificationTimestamp: "2m ago",
    },
    pdp: {
      breadcrumb: "All Journeys",
      biomarkersIncluded: "{{count}} biomarkers included",
      checkout: "Checkout",
      allInclusive: "All-inclusive · No hidden fees",
      sidebar: {
        bulletLabDraw: "Blood draw at local Quest lab",
        bulletBiomarkers: "~{{count}} biomarker analysis",
        bulletPlan: "Personalized wellness plan",
        bulletFollowUp: "Follow-up support and check-ins",
        howItWorksLabel: "How it works",
        step1Number: "1.",
        step1Title: "Book & prepare",
        step1Description:
          "After intake and lab booking, you'll visit one of our partner lab clinics for your blood draw.",
        step2Number: "2.",
        step2Title: "Receive your results and plan",
        step2Description:
          "Within about a week you'll receive a detailed interpretation and clear plan from your provider.",
        step3Number: "3.",
        step3Title: "Follow your plan",
        step3Description:
          "Follow a personalized wellness plan with actionable steps across key health areas. Retest to track your progress against your health goals.",
        trustBiomarkers: "Biomarkers selected on clinical relevance",
        trustRanges: "Functional optimal ranges to catch early warning signs",
        trustInterpretation: "Human interpretation across every result with your trusted provider",
        trustNoSubscription: "No subscription required",
        phoneMockupAlt: "Wellness plan shown on a phone screen",
      },
      whatsTested: {
        eyebrow: "What's tested",
        title: "{{count}} biomarkers",
        markers: "{{count}} marker",
        markers_plural: "{{count}} markers",
      },
      whatToExpect: {
        eyebrow: "Typical Journey timeline",
        title: "What to expect",
        day0Timeframe: "Day 0",
        day0Title: "Lab booking & intake",
        day0Description:
          "Complete your {{patientTerm}} intake and schedule your blood draw appointment",
        day1to5Timeframe: "Day 1-5",
        day1to5Title: "Lab test",
        day1to5Description: "Visit a partner lab clinic for your blood draw",
        day7to10Timeframe: "Day 7-10",
        day7to10Title: "Results ready",
        day7to10Description: "Your provider reviews and analyzes using functional optimal ranges",
        week2Timeframe: "Week 2",
        week2Title: "Wellness plan delivered",
        week2Description: "Personalized protocol across six pillars of functional health",
        month3to6Timeframe: "Month 3-6",
        month3to6Title: "Personalized follow-up",
        month3to6Description: "Check in on progress against your identified goals",
      },
    },
    pdpFaq: {
      eyebrow: "Common Questions",
      title: "FAQ",
      resultsTimeQuestion: "When will I receive my results?",
      resultsTimeAnswer:
        "You'll receive an email once your results are ready, typically within 10 days. Results include an explanation across each health area, so you can understand your full health picture, not just numbers on a page.",
      wellnessPlanQuestion: "What's included in my wellness plan?",
      wellnessPlanAnswer:
        "Your wellness plan includes an easy-to-read explanation of your lab results, plus recommendations across nutrition, exercise, stress management, and high quality supplements. Everything you need to take control of your health and reach your goals.",
      supplementsQuestion: "How do I order my supplements?",
      supplementsAnswer:
        "If your plan includes supplements to help bring your markers back into range, you can order them directly from your wellness plan.",
      afterPlanQuestion: "What happens after I finish my plan?",
      afterPlanAnswer:
        "Each Journey comes with built-in next steps, so you know what to expect. Once your plan is finished, you'll receive a notification — whether it's re-testing key markers, an updated supplement recommendation, or a follow-up appointment to review your progress. It's your chance to see how far you've come and map out what's next.",
      bloodDrawQuestion: "Where do I go for my blood draw?",
      bloodDrawAnswer:
        "Blood draws are completed at Quest Diagnostics locations. There are over 2,000 locations across the U.S. — most within 10 miles of your home. After you purchase your test, you can book a blood draw at the location that works best for you.",
    },
    sectionError: {
      message: "This section couldn't be loaded. Please try refreshing the page.",
    },
    footer: {
      description:
        "Health Journeys designed by {{-clinicName}}. Testing, interpretation, and a personalized plan — all in one.",
      explore: "Explore",
      browseJourneys: "Browse Journeys",
      howItWorks: "How It Works",
      company: "Company",
      privacy: "Privacy",
      copyright: "© {{year}} {{-clinicName}}. All rights reserved.",
    },
  },
  journeyOfferPage: {
    journeyWith: "Journey with {{-pracName}}",
    title: "The first step on your personal health Journey",
    description:
      "Comprehensive blood panels with a personalized interpretation and action plan to help you live healthier, longer.",
    biomarkers: "<strong>{{count}}</strong> biomarker",
    biomarkers_plural: "<strong>{{count}}</strong> biomarkers",
    personalizedPlan:
      "A personalized plan created by <strong>{{-pracName}}</strong> that evolves with you",
    lifetimeAccess: "<strong>Lifetime access</strong> to results tracked over time",
    hsaEligible: "HSA/FSA eligible",
    oneTimeFee: "One time fee no hidden costs",
    getStarted: "Get started",
    biomarkersPopover: {
      title: "Biomarkers included",
    },
    dontWait: "Don't wait. Starting your preventative Journey is simple",
    howItWorks: "How it works",
    labTesting: "Lab testing",
    labTestingDescription:
      "Order your lab panel focused on the areas that matter most. No waiting rooms. No prescription needed. Just real data, fast.",
    resultsExplainedStep: "Results, clearly explained",
    resultsExplainedDescriptionStep:
      "You'll be able to view and track your results, see what's in range, what's not, and what it means for your health. An expert practitioner translates the data into plain language and clear insights. ",
    personalizedPlanStep: "Personalized Wellness Plan",
    personalizedPlanDescriptionStep:
      "You'll get a personalized wellness plan built around your results. This can include supplements, nutrition, and lifestyle changes. Designed by your practitioner to be realistic, actionable, and effective.",
    retestProgressStep: "Re-test and track progress",
    retestProgressDescriptionStep:
      "Health isn't static, and neither is your plan. Re-testing gives you and your practitioner a clear picture of what's working and what to adjust, so your progress stays on track.",
    footerTitle:
      "This is the first step toward understanding your health more deeply — and taking control, <italic>together with {{-pracName}}</italic>",
    poweredBy: "Powered by",
  },
  JourneysList: {
    title: "Journeys Assistant",
    copyWebsiteUrl: "Copy website URL",
    invitePatient: "Invite {{patientTerm}} to a Journey",
    manage: "Manage",
    editJourneyPanels: "Edit journey panels",
    viewWebsite: "View website",
    previewJourneysExperience: "Preview the journeys experience",
  },
  JourneysFloatingBar: {
    patientDetailsButtonText: "Next",
    interpretationDetailsBuildPlan: "Build Wellness Plan",
    interpretationDetailsReviewPlan: "Review Wellness Plan",
    patientDetailsTextPrimary: "Next: Review Lab Results and Interpretation",
    patientDetailsTextSecondary:
      "The lab interpretation will <strong>not</strong> be shared with the {{patientTerm}} yet",
    interpretationDetailsTextPrimary: "Next: Review Wellness Plan",
    interpretationDetailsNotYetShared:
      "The lab interpretation will <strong>not</strong> be shared with the {{patientTerm}} yet",
    interpretationDetailsAlreadyShared:
      "The lab interpretation has already been shared with the {{patientTerm}}",
  },
  reminder: {
    successEmailSent: "Reminder email sent",
  },
  WomensHormones: {
    menopauseStatus: {
      pre: "Pre-menopause",
      peri: "Peri-menopause",
      post: "Post-menopause",
    },
    menstrualCyclePhase: {
      follicular: "Follicular Phase",
      luteal: "Luteal Phase",
    },
    WomensHormonesRangeUsed: "Reference range used (based on women's hormones survey)",
  },
};

export { journeys };
